import type { ApprovalItem, RegistryState, ReportRun } from '../../data/types';
import { date, nowStamp } from '../../lib/format';
import { addApproval, addCommunication, addDocuments, withAudit } from '../helpers';

export function submitReportRun(state: RegistryState, action: { run: ReportRun }): RegistryState {
  const { run } = action;
  let next: RegistryState = { ...state, reportRuns: [run, ...state.reportRuns] };
  next = addApproval(next, {
    kind: 'INVESTOR_REPORT',
    refId: run.id,
    title: `${run.title} to ${run.holderIds.length} investors`,
    summary: `${run.reportFile}${run.includeStatement ? ' plus a personal statement for each investor' : ''}, ${run.delivery === 'APP_ONLY' ? 'in the app only' : 'by email and in the app'}.`,
  });
  return withAudit(
    next,
    'Investor report submitted for approval',
    run.title,
    `${run.holderIds.length} investors`,
  );
}

export function applyReportRun(
  state: RegistryState,
  item: ApprovalItem,
  approve: boolean,
  approver: string,
): RegistryState {
  const run = state.reportRuns.find((candidate) => candidate.id === item.refId);
  if (!run) return state;
  const status: ReportRun['status'] = approve ? 'SENT' : 'REJECTED';
  let next: RegistryState = {
    ...state,
    reportRuns: state.reportRuns.map((candidate) =>
      candidate.id === run.id
        ? { ...candidate, status, approvedBy: approver, sentAt: approve ? nowStamp() : undefined }
        : candidate,
    ),
  };
  if (!approve) return next;
  next = addDocuments(
    next,
    run.holderIds.flatMap((holderId) => [
      {
        holderId,
        kind: 'INVESTOR_REPORT' as const,
        name: run.reportFile.replace(/\.pdf$/i, ''),
        date: run.quarterEnd,
        sizeKb: 2400,
        visibleToInvestor: true,
        refId: run.id,
      },
      ...(run.includeStatement
        ? [
            {
              holderId,
              kind: 'PERIODIC_STATEMENT' as const,
              name: `Investor statement as at ${date(run.quarterEnd)}`,
              date: run.quarterEnd,
              sizeKb: 140,
              visibleToInvestor: true,
              refId: run.id,
            },
          ]
        : []),
    ]),
  );
  return addCommunication(next, {
    template: 'INVESTOR_REPORT',
    subject: run.title,
    audience: `${run.fundIds.length === 1 ? state.funds.find((fund) => fund.id === run.fundIds[0])?.code : `${run.fundIds.length} funds`} investors`,
    fundId: run.fundIds.length === 1 ? run.fundIds[0] : undefined,
    holderIds: run.holderIds,
    channels: run.delivery === 'APP_ONLY' ? ['PUSH'] : ['EMAIL', 'PUSH'],
  });
}
